import { AnimatePresence } from "framer-motion";
import { useCallback, useMemo } from "react";

import { IntegrationCodeSnippet } from "@/components/flow/shared/Deploy/IntegrationCodeSnippet";
import { generateIntegrationCMS } from "@/components/flow/shared/Deploy/documentation/generateIntegrationCMS";
import {
    ImageDeployment,
    VideoDeployment,
    DataMode
} from "@/components/flow/shared/Deploy/documentation/types";
import { useDeployContext } from "@/contexts/DeployContext";
import { GetAPIKeyButton } from "../../../shared/GetAPIKeyButton";
import FilePreview from "@/components/file/Preview";
import { FileCarousel } from "@/components/file/Carousel/FileCarousel";
import { useCoreApp } from "@/contexts/CoreAppContext";
import { BoundingBoxStrategyImpl } from "@utils/annotationStrategies/BoundingBox";
import { FilePreviewProvider } from "@/contexts/FilePreviewProvider";
import { useTestWithMoreFiles } from "@/useTestWithMoreFiles";
import { SuccessBanner } from "@/components/flow/deploy/Steps/Deploy/SuccessBanner";
import { useCurrentMedia } from "@/store";

// TODO: introduce useFilePreview hook here
const TestFilesVisualAreaContent = () => {
    const { showLabels } = useCoreApp();
    const { testWithMoreFiles } = useTestWithMoreFiles();
    const currentMedia = useCurrentMedia();

    const onAddMediaItems = useCallback(
        async (files: File[], onFinally?: () => void) => {
            await testWithMoreFiles(files);

            onFinally?.();
        },
        [testWithMoreFiles]
    );

    return (
        <AnimatePresence mode="wait">
            <div className="visualArea relative flex h-full w-full flex-col">
                <FilePreviewProvider>
                    <FileCarousel className="h-full w-full" onAddMediaItems={onAddMediaItems}>
                        <FilePreview
                            key={`file-${currentMedia?.file.id}`}
                            forceHideLabels={!showLabels}
                            strategy={BoundingBoxStrategyImpl}
                        />
                    </FileCarousel>
                </FilePreviewProvider>
            </div>
        </AnimatePresence>
    );
};

export const PreviewVisualAreaContent = () => {
    const { workflowCredentials, dataMode, workflow } = useDeployContext();

    // Generate copy for IMAGE and VIDEO data modes
    const copy = useMemo(() => {
        // Don't generate copy for PHONE or TEST_FILES mode
        if (dataMode === DataMode.PHONE || dataMode === DataMode.TEST_FILES) {
            return null;
        }

        const integrationCMS = generateIntegrationCMS(workflowCredentials);

        // Get the appropriate deployment type based on data mode
        const getDefaultDeploymentType = () => {
            if (dataMode === DataMode.IMAGE) {
                return ImageDeployment.SINGLE;
            } else if (dataMode === DataMode.VIDEO) {
                return VideoDeployment.FILE;
            }
            return ImageDeployment.SINGLE;
        };

        const selectedDeploymentType = getDefaultDeploymentType();
        return integrationCMS[dataMode][
            selectedDeploymentType as keyof (typeof integrationCMS)[typeof dataMode]
        ];
    }, [workflowCredentials, dataMode]);

    // Render based on data mode
    if (dataMode === DataMode.TEST_FILES) return <TestFilesVisualAreaContent />;

    if (dataMode === DataMode.PHONE) {
        return (
            <AnimatePresence mode="wait">
                <div className="flex items-center justify-center">
                    <div className="neumorphic-shadow h-[600px] w-80 rounded-[40px] border-black/50 bg-gray-100 p-4">
                        <div className="flex h-full w-full flex-col items-center justify-center gap-6 rounded-3xl bg-white">
                            <div className="flex flex-col items-center justify-center gap-2">
                                <h3 className="text-xl font-semibold text-primary">
                                    Scan your QR code
                                </h3>
                                <p className="text-sm font-normal text-tertiary">
                                    Use your model on your phone camera.
                                </p>
                            </div>

                            {/* Dummy QR Code with GetAPIKey Button */}
                            <div className="relative">
                                {/* Blurred dummy QR code */}
                                <div className="flex h-40 w-40 items-center justify-center rounded-lg bg-gray-50 blur-sm">
                                    <img
                                        src="/images/qr_roboflow.png"
                                        alt="QR Code"
                                        className="h-full w-full object-contain"
                                    />
                                </div>

                                {/* GetAPIKey button positioned on top */}
                                <div className="absolute inset-0 flex items-center justify-center">
                                    <GetAPIKeyButton />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </AnimatePresence>
        );
    }

    return (
        <AnimatePresence mode="wait">
            <div className="visualArea h-full">
                <SuccessBanner />
                <div className="relative h-full overflow-y-hidden">
                    {copy && <IntegrationCodeSnippet copy={copy} blurred={!workflow} />}
                </div>
            </div>
        </AnimatePresence>
    );
};
